import React, { useState, useEffect } from 'react';
import { ClipboardList, Clock, CheckCircle2, AlertCircle, Send, Eye, FileText, X, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import api from '../../api';

const STATUS = {
  graded:    { label: 'Graded',         bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  submitted: { label: 'Submitted',      bg: 'bg-blue-100',    text: 'text-blue-700',    dot: 'bg-blue-500'    },
  late:      { label: 'Late',           bg: 'bg-rose-100',    text: 'text-rose-700',    dot: 'bg-rose-500'    },
  pending:   { label: 'Pending',        bg: 'bg-amber-100',   text: 'text-amber-700',   dot: 'bg-amber-500'   },
  overdue:   { label: 'Overdue',        bg: 'bg-red-100',     text: 'text-red-700',     dot: 'bg-red-500'     },
};

const SUBJECT_COLORS = {
  Mathematics: 'from-blue-500 to-blue-400',
  English:     'from-violet-500 to-violet-400',
  Physics:     'from-orange-500 to-amber-400',
  Biology:     'from-emerald-500 to-green-400',
  Chemistry:   'from-pink-500 to-rose-400',
  History:     'from-amber-500 to-yellow-400',
};

function getSubjectGradient(name = '') {
  for (const key of Object.keys(SUBJECT_COLORS)) {
    if (name.toLowerCase().includes(key.toLowerCase())) return SUBJECT_COLORS[key];
  }
  return 'from-slate-400 to-slate-500';
}

const isOverdue = (due) => due && new Date(due) < new Date();

export default function StudentAssignmentsPage() {
  const [assignments, setAssignments] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [viewAsgn,    setViewAsgn]    = useState(null);
  const [filter,      setFilter]      = useState('all');

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await api.get('/assignments/student');
      setAssignments(res.data || []);
    } catch { toast.error('Failed to load assignments'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const total     = assignments.length;
  const pending   = assignments.filter(a => !a.submitted).length;
  const submitted = assignments.filter(a => a.submitted && a.my_submission?.status !== 'graded').length;
  const graded    = assignments.filter(a => a.my_submission?.status === 'graded').length;

  const filtered = filter === 'all' ? assignments
    : filter === 'pending'   ? assignments.filter(a => !a.submitted)
    : filter === 'submitted' ? assignments.filter(a => a.submitted && a.my_submission?.status !== 'graded')
    : assignments.filter(a => a.my_submission?.status === 'graded');

  return (
    <div className="p-4 lg:p-6 max-w-4xl mx-auto space-y-5" data-testid="student-assignments-page">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-black text-slate-900">My Assignments</h1>
        <p className="text-slate-500 text-sm mt-0.5">Complete and track your tasks</p>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Pending',   value: pending,   bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-100', icon: AlertCircle,  iconCls: 'text-amber-500'  },
          { label: 'Submitted', value: submitted, bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-100',  icon: Clock,        iconCls: 'text-blue-500'   },
          { label: 'Graded',    value: graded,    bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-100',icon: CheckCircle2, iconCls: 'text-emerald-500'},
        ].map((s) => (
          <div key={s.label} className={`${s.bg} border ${s.border} rounded-2xl p-4 flex items-center gap-3`}>
            <div className={`w-9 h-9 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm`}>
              <s.icon className={`w-4 h-4 ${s.iconCls}`} />
            </div>
            <div>
              <p className={`text-2xl font-black ${s.text} leading-none`}>{s.value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 bg-white rounded-2xl p-1.5 border border-slate-100 shadow-sm w-fit">
        {[
          { value: 'all',       label: `All (${total})` },
          { value: 'pending',   label: `Pending (${pending})` },
          { value: 'submitted', label: `Submitted (${submitted})` },
          { value: 'graded',    label: `Graded (${graded})` },
        ].map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-colors duration-200 ${
              filter === f.value
                ? 'bg-[#2563eb] text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-800'
            }`}
            data-testid={`filter-${f.value}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-36 bg-white rounded-3xl animate-pulse border border-slate-100" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-100 py-16 text-center shadow-sm">
          <ClipboardList className="w-12 h-12 mx-auto mb-3 text-slate-200" />
          <p className="font-bold text-slate-600">No assignments here</p>
          <p className="text-sm text-slate-400 mt-1">
            {filter === 'all' ? "Your teachers haven't posted any assignments yet" : `No ${filter} assignments`}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {filtered.map((a) => {
            const sub     = a.my_submission;
            const overdueFlag = !sub && isOverdue(a.due_date);
            const statusKey = sub?.status ?? (overdueFlag ? 'overdue' : 'pending');
            const st = STATUS[statusKey] || STATUS.pending;
            const grad = getSubjectGradient(a.subject_name);

            return (
              <div
                key={a.id}
                className="bg-white rounded-3xl border border-slate-100 p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex flex-col gap-3"
                data-testid={`assignment-card-${a.id}`}
              >
                {/* Subject strip */}
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${grad} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">{a.subject_name}</p>
                    <p className="font-bold text-slate-900 text-sm truncate">{a.title}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${st.bg} ${st.text} flex-shrink-0`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                    {st.label}
                  </span>
                </div>

                {/* Deadline + score */}
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    <span>Due: {a.due_date || 'No deadline'}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {sub?.score != null ? (
                      <span className="font-bold text-[#2563eb]">{sub.score}/{a.max_score} pts</span>
                    ) : (
                      <span>{a.max_score} pts max</span>
                    )}
                  </div>
                </div>

                {/* Action */}
                <button
                  onClick={() => setViewAsgn(a)}
                  className={`w-full py-2 rounded-2xl text-sm font-bold flex items-center justify-center gap-2 transition-colors duration-150 ${
                    sub
                      ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      : 'bg-[#2563eb] text-white hover:bg-[#1d58d4] shadow-sm shadow-[#2563eb]/30'
                  }`}
                  data-testid={`assignment-${a.id}-btn`}
                >
                  {sub ? <><Eye className="w-3.5 h-3.5" />View Submission</> : <><Send className="w-3.5 h-3.5" />Submit Now</>}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {viewAsgn && (
        <AssignmentDialog
          assignment={viewAsgn}
          onClose={() => setViewAsgn(null)}
          onSuccess={() => { setViewAsgn(null); fetch(); }}
        />
      )}
    </div>
  );
}

function AssignmentDialog({ assignment, onClose, onSuccess }) {
  const sub      = assignment.my_submission;
  const [content, setContent] = useState(sub?.content || '');
  const [saving,  setSaving]  = useState(false);

  const handleSubmit = async () => {
    if (!content.trim()) { toast.error('Please write your answer before submitting'); return; }
    setSaving(true);
    try {
      await api.post(`/assignments/${assignment.id}/submit`, { content });
      toast.success(sub ? 'Submission updated!' : 'Assignment submitted!');
      // Award XP for submission (on-time bonus if before deadline)
      const isOnTime = !assignment.due_date || new Date() <= new Date(assignment.due_date);
      api.post('/student/xp/award', {
        action: isOnTime ? 'assignment_on_time' : 'assignment_submitted',
        reference_id: assignment.id,
      }).catch(() => {});
      onSuccess();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to submit'); }
    finally { setSaving(false); }
  };

  const overdueFlag = assignment.due_date && new Date(assignment.due_date) < new Date();
  const canEdit = !sub || sub.status !== 'graded';

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-black text-slate-900 pr-6">{assignment.title}</DialogTitle>
          <div className="flex gap-2 flex-wrap mt-1">
            <span className="bg-blue-50 text-[#2563eb] text-xs font-bold px-3 py-1 rounded-full">{assignment.subject_name}</span>
            <span className="bg-slate-100 text-slate-600 text-xs font-bold px-3 py-1 rounded-full">Max: {assignment.max_score} pts</span>
            {overdueFlag && !sub && <span className="bg-red-100 text-red-700 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1"><AlertCircle className="w-3 h-3" />Overdue</span>}
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {assignment.description && (
            <div className="bg-slate-50 rounded-2xl p-4">
              <p className="text-xs font-bold text-slate-400 uppercase mb-1.5">Description</p>
              <p className="text-sm text-slate-700 leading-relaxed">{assignment.description}</p>
            </div>
          )}

          {assignment.instructions && (
            <div className="bg-blue-50 rounded-2xl p-4 border border-[#2563eb]/10">
              <p className="text-xs font-bold text-[#2563eb] uppercase mb-1.5">Instructions</p>
              <p className="text-sm text-slate-700 leading-relaxed">{assignment.instructions}</p>
            </div>
          )}

          {sub?.status === 'graded' ? (
            <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-200">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-bold text-emerald-700">Graded</span>
                <span className="bg-white text-[#2563eb] font-black border border-[#2563eb]/20 px-3 py-0.5 rounded-full text-sm">{sub.score}/{assignment.max_score}</span>
              </div>
              {sub.feedback && <p className="text-sm text-slate-600 italic">"{sub.feedback}"</p>}
              <div className="mt-3 pt-3 border-t border-emerald-200">
                <p className="text-xs text-slate-500 font-bold mb-1">Your answer:</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{sub.content}</p>
              </div>
            </div>
          ) : (
            <div>
              <label className="text-sm font-bold text-slate-700 mb-2 block">
                {sub ? 'Update your answer:' : 'Write your answer:'}
              </label>
              <textarea
                value={content}
                onChange={e => setContent(e.target.value)}
                disabled={!canEdit}
                rows={6}
                placeholder="Type your answer here..."
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 focus:border-[#2563eb] resize-none disabled:opacity-60 disabled:bg-slate-50 leading-relaxed"
              />
              <p className="text-xs text-slate-400 mt-1">{content.length} characters</p>
            </div>
          )}

          <div className="flex gap-3">
            {canEdit && sub?.status !== 'graded' && (
              <button
                onClick={handleSubmit}
                disabled={saving || !content.trim()}
                className="flex-1 bg-[#2563eb] hover:bg-[#1d58d4] text-white font-bold py-2.5 rounded-2xl text-sm flex items-center justify-center gap-2 disabled:opacity-50 active:scale-95 transition-transform shadow-sm"
                data-testid="submit-assignment-btn"
              >
                <Send className="w-4 h-4" />
                {saving ? 'Submitting...' : sub ? 'Update Submission' : 'Submit Assignment'}
              </button>
            )}
            <button
              onClick={onClose}
              className={`${sub?.status === 'graded' ? 'w-full' : 'flex-1'} bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-2.5 rounded-2xl text-sm transition-colors`}
            >
              Close
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
